/**
 * QNH in both units, the preferred one first and the other in brackets.
 *
 * Asked for so a pilot never has to convert in their head: a METAR from
 * Argentina says `Q1013` and an American-built altimeter's Kollsman window is
 * in inches; a pilot who set the app to inches reads `A2992` off an American
 * report and a Spanish-language briefing gives hectopascals. Both figures, one
 * line: `QNH 1013 hPa (29.91 inHg)`.
 *
 * ## The conversion
 *
 * 1 inHg = 33.8639 hPa (the conventional value, mercury at 0 °C under standard
 * gravity; NIST SP 811 and WMO No. 8). `core/units/convert.ts` holds the factor
 * and the decoder already carries both figures on every report, so nothing is
 * converted here — this only decides how each figure is *written*.
 *
 * ## Rounded down, both ways
 *
 * ICAO Doc 8896 has QNH reported in whole hectopascals **rounded down**, and
 * the same direction is kept for a converted inch figure. It is the
 * conservative direction: an altimeter set a fraction low reads a fraction
 * low, which puts the aircraft *above* what it shows rather than below. A
 * figure the report stated exactly (`Q1013`, `A2992`) is unchanged by it — the
 * small epsilon is there so floating-point `1012.9999…` is not floored to 1012.
 *
 * Pure: the caller passes the pilot's preferred unit.
 */

export type PressureUnit = 'hpa' | 'inhg'

const EPSILON = 1e-6

/** Whole hectopascals, rounded down. */
export function qnhHpaFigure(hpa: number): number {
	return Math.floor(hpa + EPSILON)
}

/** Inches of mercury to two decimals, rounded down. */
export function qnhInHgFigure(inHg: number): number {
	return Math.floor(inHg * 100 + EPSILON) / 100
}

export interface QnhSource {
	altimeterHpa: number | null
	altimeterInHg: number | null
}

/**
 * The two halves, already formatted, or null when the report carries no QNH.
 *
 * `primary` is in the pilot's unit and `secondary` in the other, with their
 * unit symbols — which are identifiers and are not translated.
 */
export function qnhPair(
	source: QnhSource | null | undefined,
	preferred: PressureUnit,
): { primary: string; secondary: string } | null {
	if (!source || source.altimeterHpa === null || source.altimeterInHg === null) return null
	const hpa = `${qnhHpaFigure(source.altimeterHpa)} hPa`
	const inHg = `${qnhInHgFigure(source.altimeterInHg).toFixed(2)} inHg`
	return preferred === 'inhg' ? { primary: inHg, secondary: hpa } : { primary: hpa, secondary: inHg }
}

/** `1013 hPa (29.91 inHg)` — without the `QNH` in front, which the caller places. */
export function formatQnh(source: QnhSource | null | undefined, preferred: PressureUnit): string | null {
	const pair = qnhPair(source, preferred)
	return pair ? `${pair.primary} (${pair.secondary})` : null
}
